import {
  Component,
  Inject,
  OnInit,
  OnDestroy,
  ComponentRef,
  ViewChild,
} from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { FormControl, FormGroup, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { isEmpty, merge } from 'lodash';
import { SelectItemGroup } from 'primeng/api';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { combineLatest, of, Observable, Subject, takeUntil, forkJoin } from 'rxjs';
import { catchError, distinctUntilChanged, finalize, mergeMap, take, tap } from 'rxjs/operators';
import { Schema } from 'app/hub-components/models/analytics-api/schema.model';
import { Logger } from '../../services/logger/logger.service';
import { SharingService } from '../../services/sharing/sharing.service';
import { TrackingService } from '../../services/tracking/tracking.service';
import { WINDOW } from '../../variables';

@Component({
  selector: 'share-modal',
  styleUrls: ['./share-modal.component.less'],
  templateUrl: './share-modal.component.html',
})
export class ShareModalComponent implements OnInit, OnDestroy {
  @ViewChild(NgComponentOutlet, { static: false }) urlModifierComponentOutlet: NgComponentOutlet;
  public urlModifierComponent: ComponentRef<UrlModifierForShareModal>;

  VIEWER = 'VIEWER';
  EDITOR = 'EDITOR';
  REMOVE = 'REMOVE';
  REPORT = 'report';
  DASHBOARD = 'dashboard';
  DISCOVERY = 'discovery';
  SNAPSHOT = 'snapshot';

  public DEFAULT_PASSWORD_LENGTH = 8;

  public allowPublic: boolean = true;
  public allowTeamSharing: boolean = true;
  public objectToShare: any;
  public type: string;
  public datasets: string[] = [];
  public schemas: Record<string, Schema> = {};
  public sharePublicOptions: any[] = [
    { label: 'Off', value: false },
    { label: 'On', value: true },
  ];

  public loadingUsersAndSuites: boolean = false;
  public sendingInvites: boolean = false;
  public publicUrlCopied: boolean = false;
  public shareUrlCopied: boolean = false;
  public passwordCopied: boolean = false;
  public passwordMode: 'summary' | 'edit' = 'edit';
  public passwordStatus: 'idle' | 'saving' | 'updated' = 'idle';
  public passwordSavedInThisSession: boolean = false;
  public hasExistingPassword: boolean = false;
  public publicUrl: string;
  public shareUrl: string;
  public sharedUsersAndSuites: any[] = [];
  public groupedUsersAndSuites: SelectItemGroup[];
  public loadingPublicUrl: boolean = true;
  public note: any;

  public sharePublicControl: FormControl = new FormControl(false);
  public passwordProtectionControl: FormControl = new FormControl(false);
  public passwordControl: FormControl = new FormControl(null, [Validators.minLength(this.DEFAULT_PASSWORD_LENGTH)]);
  public usersAndSuitesControl: UntypedFormControl = new UntypedFormControl([]);
  public permissionsForm = new UntypedFormGroup({
    dates: new UntypedFormControl(),
    global_filters: new UntypedFormControl(),
  });

  public snapshotPermissionsForm = new FormGroup({
    comparisons: new FormControl(),
    views: new FormControl('both'),
    breakdownCostBasedMetrics: new FormControl(),
    breakdownViewabilityMetrics: new FormControl(),
    datasets: new FormGroup({}),
  });

  public viewOptions = [
    { label: 'Overview', value: 'overview' },
    { label: 'Breakdown', value: 'breakdown' },
    { label: 'Overview & Breakdown', value: 'both' },
  ];

  public dropdownCustomModel = {};
  public dropdownCustomOptions: object[] = [
    { name: 'Viewer', value: this.VIEWER },
    { name: 'Editor', value: this.EDITOR },
    { name: 'Remove', value: this.REMOVE },
  ];

  public editorUsersAndSuites: string[] = [];
  public removingAllUsersAndSuites: boolean = false;
  public updatingUserPermissions: { [key: string]: boolean } = {};

  private typeRequestMap = {
    [this.REPORT]: 'reports',
    [this.DASHBOARD]: 'dashboards',
    [this.DISCOVERY]: 'discoveries',
  };

  private potentialUsersAndSuites: { id: string, label: string, name: string, email?: string, value: string, type?: string }[] = [];
  private permissions: any = {};
  private nonPublicSharedObject: any;
  private destroyed$ = new Subject<void>();

  constructor(
    private trackingService: TrackingService,
    private sharingService: SharingService,
    private ref: DynamicDialogRef,
    private config: DynamicDialogConfig,
    private logger: Logger,
    @Inject(WINDOW) private window: Window,
  ) {
    this.usersAndSuitesControl.disable();
  }

  ngOnInit() {
    this.objectToShare = this.config.data?.objectToShare;
    this.allowPublic = this.config.data?.allowPublic ?? true;
    this.allowTeamSharing = this.config.data?.allowTeamSharing ?? true;
    this.type = this.config.data?.type;
    this.datasets = this.config.data?.datasets ?? [];
    this.schemas = this.config.data?.schemas ?? {};
    this.urlModifierComponent = this.config.data?.urlModifierComponent;

    if (this.type === this.SNAPSHOT) {
      this.initializeDatasetsFormGroup();
    }

    this.sharePublicControl.valueChanges.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
    ).subscribe(() => {
      this.shareToggle();
    });

    this.passwordProtectionControl.valueChanges.pipe(
      takeUntil(this.destroyed$),
      distinctUntilChanged(),
    ).subscribe((isEnabled: boolean) => {
      if (isEnabled) {
        this.passwordMode = 'edit';
      }
      if (!isEnabled && this.sharePublicControl.value) {
        this.trackingService.track('Disable password protection for public link', { type: this.type });
        this.deletePassword();
      }
    });

    if (this.allowTeamSharing) {
      this.loadingUsersAndSuites = true;
      const sharedObjectObservable: Observable<any> = this.objectToShare.shareUuid ? this.sharingService.getSharedObject(this.objectToShare.shareUuid) : of(null);
      const appUsersObservable: Observable<any[]> = this.sharingService.getAppUsers();
      const accountSuitesObservable: Observable<any[]> = this.sharingService.getAppSuites();
      const loadEditorObservable: Observable<string[]> = this.typeRequestMap[this.type] ? this.sharingService.loadEditors(this.typeRequestMap[this.type], this.objectToShare.id) : of(null);

      combineLatest([
        sharedObjectObservable.pipe(catchError(() => of(null))),
        appUsersObservable,
        accountSuitesObservable,
        loadEditorObservable,
      ]).pipe(finalize(() => {
        this.usersAndSuitesControl.enable();
        this.loadingUsersAndSuites = false;
      }))
        .subscribe(([sharedObject, users, suites, editorUsersAndSuites]: [any, any[], any[], string[]]) => {
          if (!sharedObject) {
            this.objectToShare.shareUuid = undefined;
          }
          this.nonPublicSharedObject = sharedObject;
          if (this.objectToShare.shareUuid) {
            this.shareUrl = this.sharingService.generateShareUrl(this.objectToShare.shareUuid);
          }
          this.permissions = merge(this.permissions, sharedObject && sharedObject['payload'] || {});
          this.permissionsForm.patchValue(this.permissions, { emitEvent: false });
          this.potentialUsersAndSuites = (suites || []).concat(users || []);
          this.editorUsersAndSuites = editorUsersAndSuites;
          this.setSharedUsersAndSuites();
          this.loadEditorPermissions();
        });
    }

    if (this.objectToShare.publicUuid) {
      this.sharingService.loadPublicLink(this.objectToShare.publicUuid)
        .pipe(
          finalize(() => this.loadingPublicUrl = false),
        ).subscribe(obj => {
          this.publicUrl = obj.shareUrl;
          this.sharePublicControl.patchValue(obj.public, { emitEvent: false });
          this.passwordProtectionControl.setValue(obj.passwordProtected, { emitEvent: false });
          this.hasExistingPassword = !!obj.passwordProtected;
          this.passwordMode = this.hasExistingPassword ? 'summary' : 'edit';
          this.permissions = merge(this.permissions, obj.permissions);
          this.permissionsForm.patchValue(this.permissions, { emitEvent: false });
          const formattedPermissions = {
            ...this.objectToShare.sharingPermissions,
            views: this.getViewsDropdownValue(
              this.objectToShare.sharingPermissions?.overview,
              this.objectToShare.sharingPermissions?.breakdown,
            ),
          };
          this.snapshotPermissionsForm.patchValue(formattedPermissions, { emitEvent: false });

          if (this.objectToShare.sharingPermissions?.datasets) {
            this.datasetsFormGroup.patchValue(this.objectToShare.sharingPermissions.datasets, { emitEvent: false });
          }
        });
    } else {
      this.loadingPublicUrl = false;
    }

    this.permissionsForm.valueChanges.pipe(
      takeUntil(this.destroyed$),
    ).subscribe(permissions => {
      this.updatePermissions(permissions);
    });

    this.snapshotPermissionsForm.valueChanges.pipe(
      takeUntil(this.destroyed$),
    ).subscribe((permissions) => {
      const convertedPermissions = {
        ...permissions,
        views: this.convertViewsDropdownToFormGroup(permissions.views),
      };
      this.sharingService.updateSnapshotPermissions(this.objectToShare, convertedPermissions);
    });
  }

  public ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  updatePermissions(permissions) {
    this.permissions = merge({ shared_object_id: this.objectToShare.id }, permissions);

    if ([this.DASHBOARD, this.REPORT, this.DISCOVERY, this.SNAPSHOT].includes(this.type)) {
      this.permissions = merge(this.permissions, this.getInitialPermissions());
    }

    this.sharingService.updatePermissions(this.public, this.objectToShare.publicUuid, this.nonPublicSharedObject, this.permissions);
    this.trackingService.track(`${this.type} Share Modal Permissions`, { value: this.permissions });
  }

  getInitialPermissions() {
    if (this.type === this.DASHBOARD) {
      return { dashboard_id: this.objectToShare.id };
    }

    if (this.type === this.REPORT) {
      return { report_id: this.objectToShare.id };
    }

    if (this.type === this.DISCOVERY) {
      return { discovery_id: this.objectToShare.id };
    }

    if (this.type === this.SNAPSHOT) {
      return { snapshot_id: this.objectToShare.id, dates: true, selection: true, order: true };
    }
  }

  setSharedUsersAndSuites() {
    if (this.nonPublicSharedObject && this.potentialUsersAndSuites) {
      this.sharedUsersAndSuites = this.potentialUsersAndSuites.filter(userOrSuite => this.nonPublicSharedObject['invites'].includes(userOrSuite.id));
    }
    this.initGroupedUsersAndSuites();
  }

  usersAndSuitesCleared() {
    this.trackingService.track('Clear all users and suites in share modal');
  }

  removeAllUsersAndSuites() {
    this.removingAllUsersAndSuites = true;
    this.trackingService.track(`Remove all users and suites from shared ${this.type}`);

    const removalObservables = this.sharedUsersAndSuites.map(userOrSuite =>
      this.sharingService.removeUserFromSharedObject(this.objectToShare.shareUuid, userOrSuite.id).pipe(
        tap(() => this.updateUserPermission(this.REMOVE, userOrSuite.id)),
      ),
    );

    forkJoin(removalObservables)
      .subscribe({
        next: () => {
          this.removingAllUsersAndSuites = false;
          this.sharedUsersAndSuites = [];
          this.initGroupedUsersAndSuites();
          delete this.objectToShare.shareUuid;
          this.nonPublicSharedObject = undefined;
        },
        error: (error) => {
          this.logger.error('Failed to remove all users and suites', error);
          this.removingAllUsersAndSuites = false;
        },
      });
  }

  sendInvite(skip = false) {
    const usersAndSuites = this.usersAndSuitesControl.value;
    const users = usersAndSuites.filter(uas => uas.type === 'user');
    const suites = usersAndSuites.filter(uas => uas.type === 'suite');
    let observable;
    if (this.objectToShare.shareUuid) {
      if (users.length) {
        this.trackingService.track(`Add user to shared ${this.type}`, { users: users.length });
      }
      if (suites.length) {
        this.trackingService.track(`Add suite to shared ${this.type}`, { suites: suites.length });
      }
      observable = this.sharingService.updateSharedObject(this.objectToShare.shareUuid, usersAndSuites, this.note, skip);
    } else {
      this.trackingService.track(`Create shared ${this.type}`, { users: users.length, suites: suites.length });
      observable = this.sharingService.createSharedObject(this.objectToShare.id, this.permissions, usersAndSuites, this.note, skip);
    }
    this.sendingInvites = true;
    observable.subscribe(sharedObject => {
      this.nonPublicSharedObject = sharedObject;
      this.sendingInvites = false;
      this.objectToShare.shareUuid = sharedObject['uuid'];
      this.shareUrl = this.sharingService.generateShareUrl(this.objectToShare.shareUuid);
      this.setSharedUsersAndSuites();
      this.usersAndSuitesControl.setValue([]);
      this.note = '';
      this.loadEditorPermissions();
    });
  }

  removeUserOrSuite(userOrSuite) {
    this.trackingService.track(`Remove ${userOrSuite.type} from shared ${this.type}`);
    userOrSuite['loading'] = true;
    this.sharingService.removeUserFromSharedObject(this.objectToShare.shareUuid, userOrSuite.id)
      .pipe(finalize(() => {
        userOrSuite['loading'] = false;
        this.initGroupedUsersAndSuites();
      }))
      .subscribe(() => {
        this.sharedUsersAndSuites = this.sharedUsersAndSuites.filter(item => item !== userOrSuite);

        if (this.sharedUsersAndSuites.length === 0) {
          delete this.objectToShare.shareUuid;
          this.nonPublicSharedObject = undefined;
        }
      });

    this.updateUserPermission(this.REMOVE, userOrSuite.id);
  }

  shareToggle() {
    if (this.public) {
      this.trackingService.track('Share with public link');
      this.loadingPublicUrl = true;

      if (this.type === this.SNAPSHOT && isEmpty(this.objectToShare.sharingPermissions)) {
        const snapshotPermissions = this.getInitialSnapshotPermissions();
        this.snapshotPermissionsForm.patchValue({
          views: this.getViewsDropdownValue(snapshotPermissions.views.overview, snapshotPermissions.views.breakdown),
          datasets: snapshotPermissions.datasets,
        }, { emitEvent: false });

        this.sharingService.updateSnapshotPermissions(this.objectToShare, snapshotPermissions);
      }

      if (isEmpty(this.permissions)) {
        const initial = this.getInitialPermissions();
        const defined = initial
          ? Object.fromEntries(Object.entries(initial).filter(([, v]) => v !== undefined && v !== null))
          : {};
        Object.assign(this.permissions, defined);
      }
      if (this.objectToShare.publicUuid) {
        this.trackingService.track('Create public link');
        this.sharingService.createPublicLink(this.permissions, this.objectToShare, this.type).subscribe(res => {
          this.publicUrl = this.sharingService.generatePublicShareUrl(this.objectToShare.publicUuid);
          this.loadingPublicUrl = false;
          this.permissions = merge(this.permissions, {});
          this.permissionsForm.patchValue(this.permissions, { emitEvent: false });
        });
      } else {
        this.sharingService.createPublicUuid(this.objectToShare)
          .pipe(
            mergeMap(() => this.sharingService.createPublicLink(this.permissions, this.objectToShare, this.type)),
          )
          .subscribe(() => {
            this.publicUrl = this.sharingService.generatePublicShareUrl(this.objectToShare.publicUuid);
            this.loadingPublicUrl = false;
            this.permissions = merge(this.permissions, {});
            this.permissionsForm.patchValue(this.permissions, { emitEvent: false });
          });
      }
    } else {
      this.trackingService.track('Revoke public link');
      this.sharingService.deletePublicLink(this.objectToShare.publicUuid).subscribe(() => {
        if (!this.sharedUsersAndSuites.length) {
          this.permissions = {};
          this.permissionsForm.patchValue(this.permissions, { emitEvent: false });
        }
        this.clearPasswordFormControls();
      });
    }
  }

  copyLink(url) {
    if (this.urlModifierComponentOutlet['_componentRef']) {
      const componentInstance = this.urlModifierComponentOutlet['_componentRef'].instance;
      url = componentInstance.beforeUrlCopiedCallback(url);
    }
    try {
      this.window.navigator.clipboard.writeText(url);
    } catch (error) {
      this.logger.error(`Failed to copy URL: ${url}`);
    }
    if (url.includes('sd')) {
      this.trackingService.track('Copy private link', { value: url });
      this.shareUrlCopied = true;
      setTimeout(() => this.shareUrlCopied = false, 2000);
    } else {
      this.trackingService.track('Copy public link', { value: url });
      this.publicUrlCopied = true;
      setTimeout(() => this.publicUrlCopied = false, 2000);
    }
  }

  public copyPassword(): void {
    const password = this.passwordControl.value;
    if (!password) {
      return;
    }
    try {
      this.window.navigator.clipboard.writeText(password);
      this.passwordCopied = true;
      setTimeout(() => this.passwordCopied = false, 2000);
      this.trackingService.track('Copy password');
    } catch (error) {
      this.logger.error(`Failed to copy password ${password}`);
    }
  }

  public savePassword(updateSaveButton: boolean = true): void {
    if (this.passwordControl.invalid || !this.passwordControl.value) {
      return;
    }

    if (updateSaveButton) {
      this.passwordStatus = 'saving';
    }
    const payload = { ...this.permissions, shared_object_id: this.objectToShare.id };
    this.sharingService.savePassword(this.objectToShare.publicUuid, payload, this.passwordControl.value)
      .pipe(take(1))
      .subscribe(() => {
        this.hasExistingPassword = true;
        this.passwordMode = 'summary';
        this.passwordSavedInThisSession = true;
        this.passwordStatus = 'updated';
        setTimeout(() => {
          this.passwordStatus = 'idle';
        }, 2000);
      });

    this.trackingService.track('Save password for public link', { type: this.type });
  }

  public startPasswordChange(): void {
    this.passwordMode = 'edit';
    this.passwordSavedInThisSession = false;
    this.passwordStatus = 'idle';
    this.passwordControl.markAsUntouched();
  }

  public cancelPasswordChange(): void {
    this.passwordControl.setValue(null);
    this.passwordControl.markAsUntouched();
    this.passwordMode = 'summary';
    this.passwordSavedInThisSession = false;
    this.passwordStatus = 'idle';
  }

  public trackBySharedItems(index: number, item: any): string {
    return item.id;
  }

  public dropdownModelChange(action: string, userOrSuite): void {
    if (action === this.REMOVE) {
      this.removeUserOrSuite(userOrSuite);
      return;
    }

    this.updateUserPermission(action, userOrSuite.id);
  }

  public loadEditorPermissions(): void {
    this.sharedUsersAndSuites.forEach(userOrSuite => {
      this.dropdownCustomModel[userOrSuite.id] = this.editorUsersAndSuites.includes(userOrSuite.id) ? this.dropdownCustomOptions[1] : this.dropdownCustomOptions[0];
    });
  }

  public close() {
    this.ref.close();
  }

  public get datasetsFormGroup(): FormGroup {
    return this.snapshotPermissionsForm.controls['datasets'];
  }

  public getDatasetDisplayName(datasetName: string): string {
    const schema = this.schemas[datasetName];
    return schema?.displayName ?? datasetName;
  }

  private initGroupedUsersAndSuites(): void {
    let suites = this.potentialUsersAndSuites.filter(userOrSuite => userOrSuite.type === 'suite');
    let users = this.potentialUsersAndSuites.filter(userOrSuite => userOrSuite.type === 'user');
    suites = suites.map(suite => ({ ...suite, disabled: this.sharedUsersAndSuites.some(userOrSuite => userOrSuite.id === suite.id) }));
    users = users.map(user => ({ ...user, disabled: this.sharedUsersAndSuites.some(userOrSuite => userOrSuite.id === user.id) }));

    this.groupedUsersAndSuites = [
      {
        label: 'Suites',
        value: 'suites',
        items: suites,
      },
      {
        label: 'Users',
        value: 'user',
        items: users,
      },
    ];
  }

  private initializeDatasetsFormGroup(): void {
    this.datasets.forEach(dataset => {
      this.datasetsFormGroup.addControl(dataset, new FormControl(true));
    });
  }

  private get public() {
    return this.sharePublicControl.value;
  }

  private updateUserPermission(action, userOrSuiteId): void {
    this.updatingUserPermissions[userOrSuiteId] = true;
    if (action === this.VIEWER || action === this.REMOVE) {
      this.editorUsersAndSuites = this.editorUsersAndSuites.filter(id => id !== userOrSuiteId);
      this.trackingService.track(`Removed user as editor from ${this.type}`);
      this.sharingService.removeEditor(this.typeRequestMap[this.type], this.objectToShare.id, userOrSuiteId).pipe(take(1)).subscribe(() =>{
        this.updatingUserPermissions[userOrSuiteId] = false;
      });
    }

    if (action === this.EDITOR) {
      if (!this.editorUsersAndSuites.includes(userOrSuiteId)) {
        this.editorUsersAndSuites.push(userOrSuiteId);
      }
      this.trackingService.track(`Added user as editor from ${this.type}`);
      this.sharingService.addEditor(this.typeRequestMap[this.type], this.objectToShare.id, userOrSuiteId).pipe(take(1)).subscribe(() => {
        this.updatingUserPermissions[userOrSuiteId] = false;
      });
    }
  }

  private getInitialSnapshotPermissions(): { views: { overview: boolean, breakdown: boolean }, datasets: Record<string, boolean> } {
    return {
      views: this.convertViewsDropdownToFormGroup(this.snapshotPermissionsForm.value.views),
      datasets: this.datasetsFormGroup.value,
    };
  }

  private deletePassword(): void {
    const payload = { ...this.permissions, shared_object_id: this.objectToShare.id };
    this.sharingService.savePassword(this.objectToShare.publicUuid, payload, null).pipe(take(1)).subscribe(() => {
      this.clearPasswordFormControls();
    });
  }

  private clearPasswordFormControls(): void {
    this.passwordControl.setValue(null);
    this.passwordProtectionControl.setValue(false);
    this.hasExistingPassword = false;
    this.passwordMode = 'edit';
    this.passwordSavedInThisSession = false;
    this.passwordStatus = 'idle';
  }

  private getViewsDropdownValue(overview: boolean, breakdown: boolean): string {
    if (overview && breakdown) {
      return 'both';
    } else if (overview) {
      return 'overview';
    } else if (breakdown) {
      return 'breakdown';
    }
    return 'both';
  }

  private convertViewsDropdownToFormGroup(viewsValue: string): { overview: boolean; breakdown: boolean } {
    switch (viewsValue) {
      case 'overview':
        return { overview: true, breakdown: false };
      case 'breakdown':
        return { overview: false, breakdown: true };
      case 'both':
      default:
        return { overview: true, breakdown: true };
    }
  }
}

export interface UrlModifierForShareModal extends Component {
  beforeUrlCopiedCallback: (url: string) => string;
}
